import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Plus, Trash2, Edit, UserPlus, X, Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscriberPools() {
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolMembers, setPoolMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [newPool, setNewPool] = useState({
    pool_name: '',
    description: ''
  });

  useEffect(() => {
    fetchPools();
    fetchAllSubscribers();
  }, []);

  const fetchPools = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/business/subscriber-pools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPools(response.data.pools);
    } catch (error) {
      toast.error('Failed to fetch pools');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSubscribers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/business/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data.subscribers);
    } catch (error) {
      console.error('Failed to fetch subscribers');
    }
  };

  const handleCreatePool = async () => {
    if (!newPool.pool_name.trim()) {
      toast.error('Please enter a pool name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/business/subscriber-pools`, newPool, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pool created successfully');
      setShowCreateDialog(false);
      setNewPool({ pool_name: '', description: '' });
      fetchPools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pool');
    }
  };

  const handleDeletePool = async (poolId) => {
    if (!window.confirm('Are you sure you want to delete this pool?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/business/subscriber-pools/${poolId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pool deleted successfully');
      fetchPools();
    } catch (error) {
      toast.error('Failed to delete pool');
    }
  };

  const handleViewMembers = async (pool) => {
    setSelectedPool(pool);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/business/subscriber-pools/${pool.pool_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPoolMembers(response.data.members || []);
      setShowMembersDialog(true);
    } catch (error) {
      toast.error('Failed to fetch pool members');
    }
  };

  const handleAddMember = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/business/subscriber-pools/${selectedPool.pool_id}/members/${customerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Member added successfully');
      handleViewMembers(selectedPool); // Refresh members
      fetchPools(); // Refresh pool counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/business/subscriber-pools/${selectedPool.pool_id}/members/${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Member removed successfully');
      handleViewMembers(selectedPool); // Refresh members
      fetchPools(); // Refresh pool counts
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const getAvailableSubscribers = () => {
    const memberIds = poolMembers.map(m => m.customer_id);
    return subscribers.filter(s => !memberIds.includes(s.customer_id));
  };

  const filteredSubscribers = getAvailableSubscribers().filter(sub =>
    sub.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-outfit text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/business/dashboard')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-playfair text-4xl font-bold text-primary mb-2">
                Subscriber Pools
              </h1>
              <p className="text-gray-600">
                Organize subscribers into groups for targeted promotional emails
              </p>
            </div>

            <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Pool
            </Button>
          </div>
        </div>

        {/* Pools Grid */}
        {pools.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold mb-2">No Pools Yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first subscriber pool to organize your audience
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Pool
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pools.map((pool) => (
              <Card key={pool.pool_id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{pool.pool_name}</h3>
                      <p className="text-sm text-gray-500">
                        {pool.member_count} {pool.member_count === 1 ? 'member' : 'members'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeletePool(pool.pool_id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {pool.description && (
                  <p className="text-sm text-gray-600 mb-4">{pool.description}</p>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewMembers(pool)}
                    className="flex-1"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    View Members
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Pool Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Subscriber Pool</DialogTitle>
              <DialogDescription>
                Create a new pool to organize your subscribers
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Pool Name *</Label>
                <Input
                  value={newPool.pool_name}
                  onChange={(e) => setNewPool({ ...newPool, pool_name: e.target.value })}
                  placeholder="e.g., VIP Customers, Newsletter Subscribers"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  value={newPool.description}
                  onChange={(e) => setNewPool({ ...newPool, description: e.target.value })}
                  placeholder="Describe the purpose of this pool..."
                  className="mt-2"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePool}>
                Create Pool
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Members Dialog */}
        <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedPool?.pool_name}</DialogTitle>
              <DialogDescription>
                Manage members in this pool
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowAddMemberDialog(true)}
                  size="sm"
                  className="gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Members
                </Button>
              </div>

              {poolMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No members in this pool yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {poolMembers.map((member) => (
                    <div
                      key={member.customer_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{member.customer_name}</p>
                        <p className="text-sm text-gray-500">{member.customer_email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.customer_id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Member Dialog */}
        <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Members to {selectedPool?.pool_name}</DialogTitle>
              <DialogDescription>
                Select subscribers to add to this pool
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search subscribers..."
                  className="pl-10"
                />
              </div>

              {filteredSubscribers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No available subscribers found</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredSubscribers.map((subscriber) => (
                    <div
                      key={subscriber.customer_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                    >
                      <div>
                        <p className="font-medium">{subscriber.customer_name}</p>
                        <p className="text-sm text-gray-500">{subscriber.customer_email}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddMember(subscriber.customer_id)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
